import { Input } from '@cian/ui-kit';
import * as React from 'react';
import { IRegion } from '../../../../types/geoSwitcher';
import { GeoSuggest } from '../GeoSuggest';

import * as styles from './GeoSwitcherHeader.css';

interface IGeoSwitcherHeaderProps {
  titleId: string;
  inputValue: string;
  suggests: IRegion[];
  loading: boolean;
  changeInputValue(value: string): void;
  selectRegion(region: IRegion): void;
}

export const GeoSwitcherHeader: React.FC<IGeoSwitcherHeaderProps> = ({
  titleId,
  inputValue,
  suggests,
  loading,
  changeInputValue,
  selectRegion,
}) => {
  const id = React.useId();
  const suggestsId = `${id}-suggests`;
  const [visibleSuggest, setVisibleSuggest] = React.useState<boolean>(false);
  const [activeRegion, setActiveRegion] = React.useState<IRegion | null>(null);
  const isSuggestVisible = visibleSuggest && !loading && suggests.length > 0;
  const activeIndex = isSuggestVisible && activeRegion ? suggests.indexOf(activeRegion) : -1;

  if (activeRegion && (loading || !suggests.includes(activeRegion))) {
    setActiveRegion(null);
  }

  const openSuggests = React.useCallback(() => {
    setVisibleSuggest(true);
  }, []);

  const closeSuggests = React.useCallback(() => {
    setVisibleSuggest(false);
    setActiveRegion(null);
  }, []);

  const handleChangeValue = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setActiveRegion(null);
      openSuggests();
      changeInputValue(e.target.value);
    },
    [changeInputValue, openSuggests],
  );

  const handleSelect = React.useCallback(
    (region: IRegion) => {
      closeSuggests();
      selectRegion(region);
    },
    [closeSuggests, selectRegion],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      closeSuggests();

      return;
    }

    if (e.nativeEvent.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }

    if (e.key === 'Escape' && isSuggestVisible) {
      e.preventDefault();
      e.stopPropagation();
      closeSuggests();
    } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !loading && suggests.length > 0) {
      e.preventDefault();
      openSuggests();
      const nextIndex =
        e.key === 'ArrowDown'
          ? Math.min(activeIndex + 1, suggests.length - 1)
          : activeIndex === -1
            ? suggests.length - 1
            : Math.max(activeIndex - 1, 0);
      setActiveRegion(suggests[nextIndex]);
    } else if (e.key === 'Enter' && activeIndex !== -1) {
      e.preventDefault();
      handleSelect(suggests[activeIndex]);
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['search']}>
        <div className={styles['input']}>
          <Input
            autoFocus
            role="combobox"
            aria-labelledby={titleId}
            aria-autocomplete="list"
            aria-expanded={isSuggestVisible}
            aria-controls={isSuggestVisible ? suggestsId : undefined}
            aria-activedescendant={activeIndex === -1 ? undefined : `${suggestsId}-${activeIndex}`}
            placeholder="Выберите регион или город"
            value={inputValue}
            loading={loading}
            onChange={handleChangeValue}
            onFocus={openSuggests}
            onBlur={closeSuggests}
            onKeyDown={handleKeyDown}
          />
          {isSuggestVisible && (
            <GeoSuggest
              id={suggestsId}
              labelledBy={titleId}
              activeIndex={activeIndex}
              suggests={suggests}
              onSelect={handleSelect}
            />
          )}
        </div>
      </div>
    </div>
  );
};
